(function () {
class StockistQuoteBuilder {
  constructor(root) {
    this.root = root;
    this.storageKey = 'wiggly-stockist-quote-v1';
    this.products = new Map();
    this.selection = new Map();
    this.intent = '';
    this.catalogueStep = window.matchMedia('(max-width: 767px)').matches ? 12 : 18;
    this.catalogueLimit = 6; // short first view so the form isn't buried; Show more / search reveal the rest
    this._collectProducts();
    this._restore();
    this._bind();
    this._setMinimumDate();
    this._render();
  }

  _collectProducts() {
    this.root.querySelectorAll('[data-quote-product]').forEach((card) => {
      const product = {
        id: card.dataset.productId,
        title: card.dataset.productTitle,
        image: card.dataset.productImage,
        url: card.dataset.productUrl,
        card,
      };
      this.products.set(product.id, product);
    });
  }

  _restore() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.storageKey) || '{}');
      this.intent = typeof stored.intent === 'string' ? stored.intent : '';
      Object.entries(stored.items || {}).forEach(([id, qty]) => {
        if (this.products.has(id) && Number.isFinite(Number(qty)) && Number(qty) > 0) {
          this.selection.set(id, Math.min(999, Math.round(Number(qty))));
        }
      });
    } catch (_error) {
      try { sessionStorage.removeItem(this.storageKey); } catch (_storageError) { /* Storage is optional. */ }
    }
  }

  _persist() {
    const items = {};
    this.selection.forEach((qty, id) => { items[id] = qty; });
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify({ intent: this.intent, items }));
    } catch (_error) {
      /* The builder still works when browser storage is unavailable. */
    }
  }

  _bind() {
    const picker = this.root.querySelector('[data-quote-picker]');
    const toggle = this.root.querySelector('[data-quote-start-shortlist]');
    const enquire = this.root.querySelector('[data-quote-start-enquire]');
    const setPicker = (open) => {
      if (picker) picker.hidden = !open;
      toggle?.setAttribute('aria-expanded', String(open));
      toggle?.classList.toggle('is-selected', open);
      if (open) enquire?.classList.remove('is-selected');
    };
    setPicker(this.selection.size > 0); // closed unless a shortlist is already in progress
    toggle?.addEventListener('click', () => setPicker(picker?.hidden));
    enquire?.addEventListener('click', () => { // the link itself jumps to the form
      setPicker(false);
      enquire.classList.add('is-selected');
    });

    this.root.querySelectorAll('input[name="quote-intent-choice"]').forEach((input) => {
      if (input.value === this.intent) input.checked = true;
      input.addEventListener('change', () => {
        this.intent = input.value;
        this._persist();
        const error = this.root.querySelector('[data-quote-selection-error]');
        if (error) error.hidden = true;
        this._render();
      });
    });

    this.products.forEach((product) => {
      product.card.querySelector('[data-quote-add]')?.addEventListener('click', () => {
        this.selection.set(product.id, 1);
        this._announce(`${product.title} added to your shortlist.`);
        this._changed();
      });
      product.card.querySelector('[data-quote-increase]')?.addEventListener('click', () => {
        this.selection.set(product.id, Math.min(999, (this.selection.get(product.id) || 0) + 1));
        this._changed();
      });
      product.card.querySelector('[data-quote-decrease]')?.addEventListener('click', () => {
        const next = (this.selection.get(product.id) || 0) - 1;
        if (next <= 0) {
          this.selection.delete(product.id);
          this._announce(`${product.title} removed from your shortlist.`);
        } else {
          this.selection.set(product.id, next);
        }
        this._changed();
      });
    });

    this.root.querySelector('[data-quote-search]')?.addEventListener('input', () => this._filter());
    this.root.querySelector('[data-quote-more]')?.addEventListener('click', () => {
      this.catalogueLimit += this.catalogueStep;
      this._filter();
    });

    this.root.querySelectorAll('[data-quote-continue], [data-quote-mobile-continue]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (!this.intent) {
          event.preventDefault();
          this._showSelectionError();
        }
      });
    });

    this.root.querySelector('[data-quote-budget]')?.addEventListener('change', () => this._renderIntent());

    const form = this.root.querySelector('.stockist-quote-form');
    form?.addEventListener('submit', (event) => {
      if (!this.intent) {
        event.preventDefault();
        this._showSelectionError();
        return;
      }
      this._syncFields();
    });

    if (this.root.querySelector('[data-quote-success]')) {
      try { sessionStorage.removeItem(this.storageKey); } catch (_error) { /* Storage is optional. */ }
      this.selection.clear();
      this.intent = '';
    }
  }

  _setMinimumDate() {
    const date = this.root.querySelector('[data-quote-required-date]');
    if (!date) return;
    const earliestDate = new Date();
    earliestDate.setDate(earliestDate.getDate() + 14);
    date.min = `${earliestDate.getFullYear()}-${String(earliestDate.getMonth() + 1).padStart(2, '0')}-${String(earliestDate.getDate()).padStart(2, '0')}`;
  }

  _changed() {
    this._persist();
    this._render();
  }

  _announce(message) {
    const live = this.root.querySelector('[data-quote-live]');
    if (!live) return;
    window.clearTimeout(this.announceTimeout);
    live.textContent = '';
    this.announceTimeout = window.setTimeout(() => { live.textContent = message; }, 30);
  }

  destroy() {
    window.clearTimeout(this.announceTimeout);
  }

  _filter() {
    const query = (this.root.querySelector('[data-quote-search]')?.value || '').trim().toLowerCase();
    let visible = 0;
    let matching = 0;
    this.products.forEach((product) => {
      const matches = !query || product.title.toLowerCase().includes(query);
      if (matches) matching += 1;
      const show = matches && (query || matching <= this.catalogueLimit); // searches show every match
      product.card.hidden = !show;
      if (show) visible += 1;
    });
    const empty = this.root.querySelector('[data-quote-empty]');
    if (empty) empty.hidden = matching !== 0;
    const moreWrap = this.root.querySelector('[data-quote-more-wrap]');
    const more = this.root.querySelector('[data-quote-more]');
    if (moreWrap) moreWrap.hidden = matching === 0;
    if (more) more.hidden = visible >= matching;
    const results = this.root.querySelector('[data-quote-results]');
    if (results) {
      const template = this.root.dataset.resultsTemplate;
      results.textContent = template.replace('[visible]', visible).replace('[total]', matching);
    }
  }

  _render() {
    this._renderIntent();
    this._renderCards();
    this._renderSummaries();
    this._syncFields();
    this._filter();
  }

  _renderIntent() {
    const stockist = this.intent === 'Stockist enquiry';
    const business = this.root.querySelector('[data-quote-business-name]');
    if (business) business.required = stockist;

    const below = this.root.querySelector('[data-quote-budget]')?.selectedOptions[0]?.dataset.below;
    const note = this.root.querySelector('[data-quote-below-minimum]');
    if (note) note.hidden = !(below === 'all' || (below === 'stockist' && stockist));
  }

  _renderCards() {
    this.products.forEach((product) => {
      const qty = this.selection.get(product.id) || 0;
      product.card.classList.toggle('is-selected', qty > 0);
      const add = product.card.querySelector('[data-quote-add]');
      const controls = product.card.querySelector('[data-quote-quantity]');
      if (add) add.hidden = qty > 0;
      if (controls) controls.hidden = qty === 0;
      const output = product.card.querySelector('[data-quote-quantity-value]');
      if (output) output.textContent = qty || 1;
    });
  }

  _selectionRows() {
    return [...this.selection.entries()].map(([id, qty]) => ({ ...this.products.get(id), qty }));
  }

  _totalUnits() {
    return this._selectionRows().reduce((sum, product) => sum + product.qty, 0);
  }

  _summaryItem(product, compact = false) {
    const li = document.createElement('li');
    li.className = compact ? 'stockist-summary-item stockist-summary-item--compact' : 'stockist-summary-item';

    const img = document.createElement('img');
    img.src = product.image;
    img.alt = '';
    img.width = 52;
    img.height = 52;
    img.loading = 'lazy';

    const copy = document.createElement('span');
    const title = document.createElement('strong');
    title.textContent = product.title;
    const quantity = document.createElement('small');
    quantity.textContent = `Approx. ${product.qty}`;
    copy.append(title, quantity);

    li.append(img, copy);

    if (!compact) {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = '×';
      remove.setAttribute('aria-label', `Remove ${product.title}`);
      remove.addEventListener('click', () => {
        this.selection.delete(product.id);
        this._announce(`${product.title} removed from your shortlist.`);
        this._changed();
      });
      li.append(remove);
    }
    return li;
  }

  _renderSummaries() {
    const rows = this._selectionRows();
    const total = this._totalUnits();
    const desktop = this.root.querySelector('[data-quote-summary-items]');
    const mobile = this.root.querySelector('[data-quote-mobile-items]');
    if (desktop) desktop.replaceChildren(...rows.map((product) => this._summaryItem(product)));
    if (mobile) mobile.replaceChildren(...rows.map((product) => this._summaryItem(product, true)));

    const empty = this.root.querySelector('[data-quote-summary-empty]');
    if (empty) empty.hidden = rows.length > 0;
    const totalRow = this.root.querySelector('[data-quote-summary-total]');
    if (totalRow) totalRow.hidden = rows.length === 0;
    const totalEl = this.root.querySelector('[data-quote-total-units]');
    if (totalEl) totalEl.textContent = total;
    const mobileCount = this.root.querySelector('[data-quote-mobile-count]');
    if (mobileCount) mobileCount.textContent = `${total} ${total === 1 ? 'unit' : 'units'}`;

    this.root.querySelectorAll('[data-quote-continue], [data-quote-mobile-continue]').forEach((link) => {
      const disabled = !this.intent;
      link.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      link.classList.toggle('is-disabled', disabled);
      if (!this.intent) {
        link.textContent = this.root.dataset.continueNeedsIntent;
      } else if (rows.length === 0) {
        link.textContent = this.root.dataset.continueNeedsProducts;
      } else {
        link.textContent = this.root.dataset.continueReady;
      }
    });

    const mobileTray = this.root.querySelector('[data-quote-mobile-summary]');
    if (mobileTray) mobileTray.hidden = rows.length === 0;
  }

  _syncFields() {
    const rows = this._selectionRows();
    const shortlist = rows.map((product) => `${product.title} — approximately ${product.qty}`).join('\n');
    const shortlistField = this.root.querySelector('[data-quote-shortlist-field]');
    const totalField = this.root.querySelector('[data-quote-total-field]');
    const intentField = this.root.querySelector('[data-quote-intent-field]');
    if (shortlistField) shortlistField.value = shortlist;
    if (totalField) totalField.value = String(this._totalUnits());
    if (intentField) intentField.value = this.intent;
  }

  _showSelectionError() {
    const error = this.root.querySelector('[data-quote-selection-error]');
    if (error) error.hidden = false;
    const target = this.intent ? this.root.querySelector('[data-quote-builder]') : this.root.querySelector('[data-quote-intents]');
    target?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    const focusTarget = this.intent
      ? this.root.querySelector('[data-quote-search]')
      : this.root.querySelector('input[name="quote-intent-choice"]');
    focusTarget?.focus({ preventScroll: true });
  }
}

  const lifecycle = window.WigglyStockistQuoteLifecycle || {
    builders: new WeakMap(),
    rootsIn(scope) {
      return [scope.matches?.('[data-stockist-quote]') && scope, ...(scope.querySelectorAll?.('[data-stockist-quote]') || [])].filter(Boolean);
    },
    init(scope = document) {
      this.rootsIn(scope).forEach((root) => {
        if (!this.builders.has(root)) this.builders.set(root, new StockistQuoteBuilder(root));
      });
    },
    unload(scope) {
      this.rootsIn(scope).forEach((root) => {
        this.builders.get(root)?.destroy();
        this.builders.delete(root);
      });
    }
  };

  window.WigglyStockistQuoteLifecycle = lifecycle;

  if (!lifecycle.listenersBound) {
    lifecycle.listenersBound = true;
    document.addEventListener('shopify:section:load', (event) => lifecycle.init(event.target));
    document.addEventListener('shopify:section:unload', (event) => lifecycle.unload(event.target));
  }

  if (document.readyState === 'loading') {
    if (!lifecycle.domReadyBound) {
      lifecycle.domReadyBound = true;
      document.addEventListener('DOMContentLoaded', () => lifecycle.init(document));
    }
  } else {
    lifecycle.init(document);
  }
})();
